import type {CountryCode, LangCode} from '../../../feature-app/common/types';
import type {
  ContentPrefsCarInterestsOptionsType,
  ContentPrefsIHaveOptionsType,
  ContentPrefsInterestsOptionsType,
  ContentPrefsPurposeOptionType,
} from '../../../feature-app/views/content-prefs';
import {AvailableEnvironmentNames} from './config';
import type {AvailableEndpointNames} from './config';

export type EnvironmentTypes = (typeof AvailableEnvironmentNames)[number];
export type APIEndpoints = (typeof AvailableEndpointNames)[number];

export interface EnvConfig {
  endpoints: Record<APIEndpoints, string>;
  apiToken: string;
  client_id: string;
  client_secret: string;
}

export type MulesoftRequestTypesMap = Pick<
  {
    '/case': ICaseCreationRequest;
    '/customer-registration': ILiteRegisterRequest;
    '/unsubscribe'?: IUnsubscriptionRequest;
  },
  APIEndpoints
>;

export interface ICaseCreationRequest {
  ecid?: string;
  firstName: string;
  lastName: string;
  email: string;
  market: CountryCode;
  CountryCode: CountryCode;
  preferredLanguage: LangCode;
  subject?: string;
  message?: string;
}

export interface IUnsubscriptionRequest {
  channelToken: string;
  subscriptionID: string;
}

export interface IAPIBadResponse {
  code: number;
  info: 'SUCCESS' | 'FAILURE';
  errors: [
    {
      transactionStatus: 'FAILURE';
      errorCodes: string;
      shortDescription: string;
      severity: string;
    },
  ];
}

export interface IAPIResponseData {
  caseNumber?: string;
  CustomerId?: string;
  emailId?: string;
  id?: string;
  message?: string;
  success?: 'true' | 'false';
  token?: string;
}

export interface IAPIResponse {
  'code': number;
  'info': 'SUCCESS' | 'FAILURE';
  'response-data': IAPIResponseData;
  'errors'?: [
    {
      transactionStatus: 'FAILURE';
      errorCodes: string;
      shortDescription: string;
      severity: string;
    },
  ];
}

export function isValidAPIResponse(
  response: IAPIResponse | IAPIBadResponse,
): response is IAPIResponse {
  return `response-data` in response;
}

export const CarLineOptions = [
  `launchEdition`,
  `premium`,
  `pro`,
  `brabus`,
] as const;
export const availableContactOptions = [`email`, `sms`, `phone`] as const;

export interface ILiteRegisterRequest {
  market?: CountryCode;
  ecid?: string;
  voucher?: string;
  email?: string;
  address?: {
    mailing?: {
      street1?: string;
      city?: string;
      country?: CountryCode;
      postalCode?: string;
    };
  };
  marketingActivities?: {
    reserveMySeatSubmitted?: boolean;
    contactPreferencesSubmitted?: boolean;
    vipBuySubmitted?: boolean;
  };
  communicationPreferences?: {
    preferredCommunicationChannels: Array<
      (typeof availableContactOptions)[number]
    >;
  };
  lead?: {
    noPreference?: boolean;
    colorInterest?: string;
    salutation?: string;
    lastName?: string;
    firstName?: string;
    preferredCommunicationLanguage?: LangCode;
    phoneCode?: string;
    phone?: string;
    position?: string;
    totalFleetSize?: string;
    amountOfcars?: string;
    descriptionField?: string;
    daimlerFrameworkAgreement?: string;
    companyName?: string;
    smart_vocConsentEmail_EU?: boolean;
  };
  type?: string;
  lineItem?: Array<(typeof CarLineOptions)[number]>;
  description?: string;

  personalDetails?: {
    dateOfBirth: string;
  };
  consents?: {
    Subscriptions?: Array<{
      Name:
        | 'smart_NLwithDOI_EU'
        | 'smart_vocWithDOI_EU'
        | 'smart_B2B_NLwithDOI_EU'
        | 'smart_RMS_NLwithDOI_EU'
        | 'smart_VIP_NLwithDOI_EU';
      isSubscribed: boolean;
    }>;
    Preferences?: Array<{
      Name:
        | 'smart_termsOfService_EU'
        | 'smart_privacyPolicy_EU'
        | 'smart_ecosystem_EU';
      isConsentGranted: boolean;
    }>;
  };

  // contact preferences
  iDrive?:
    | {
        servicesCar: {
          Other: string;
          sharing: Array<ContentPrefsPurposeOptionType>;
        };
      }
    | {
        companyCar: {
          profession: string;
          company: string;
          ev: boolean;
        };
      }
    | {
        leasingCar: {
          brand: string;
          model: string;
          ev: boolean;
          iHave: Array<ContentPrefsIHaveOptionsType>;
          carInterests: ContentPrefsCarInterestsOptionsType;
        };
      }
    | {
        ownCar: {
          brand: string;
          model: string;
          ev: boolean;
          iHave: Array<ContentPrefsIHaveOptionsType>;
          carInterests: ContentPrefsCarInterestsOptionsType;
        };
      };
  interests?: Array<ContentPrefsInterestsOptionsType>;
  contentPreferencesSubmitted?: boolean;
  eventName?: string;
  productInterest?: string;
  lineInterests?: string[];
}

export function isValidEnv(env: any): env is EnvironmentTypes {
  if (
    typeof env === `string` &&
    AvailableEnvironmentNames.includes(env as EnvironmentTypes)
  ) {
    return true;
  }

  return false;
}
